import React from "react";
import { Box, Typography } from "@mui/material";
import LoadingCircularProgress from "../LoadingCircularProgress";

const NUMBER = new Intl.NumberFormat();
const cell = { px: 1.5, py: 0.75, borderBottom: 1, borderColor: "divider", whiteSpace: "nowrap", fontSize: "0.875rem" };

/**
 * The first rows of a template export, exactly as the file will have them:
 * the template's columns in its order.
 */
export const TemplatePreview = ({ preview, loading, title }) => {
  if (loading) return <LoadingCircularProgress />;
  if (!preview) return null;
  const { columns, rows, total } = preview;
  // number columns (Count, Epi Week) sit centered, header and values alike
  const centered = new Set(
    columns
      .filter((c) => rows.some((row) => row[c.key] != null && row[c.key] !== ""))
      .filter((c) => rows.every((row) => row[c.key] == null || row[c.key] === "" || typeof row[c.key] === "number"))
      .map((c) => c.key)
  );
  const align = (key) => (centered.has(key) ? "center" : "left");
  if (!total) {
    return (
      <Box sx={{ p: 6, textAlign: "center" }}>
        <Typography color="text.secondary">No specimens match these filters.</Typography>
      </Box>
    );
  }
  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%", minHeight: 0, px: { xs: 2, md: 3 }, py: 2 }}>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }} data-testid="template-preview-count">
        {total > rows.length
          ? `Showing the first ${NUMBER.format(rows.length)} of ${NUMBER.format(total)} rows.`
          : `${NUMBER.format(total)} row${total === 1 ? "" : "s"}.`}
      </Typography>
      <Box sx={{ flex: "0 1 auto", minHeight: 0, overflow: "auto", border: 1, borderColor: "divider", borderRadius: 1 }}>
        <Box component="table" sx={{ borderCollapse: "separate", borderSpacing: 0, minWidth: "100%" }}>
          <Box component="caption" sx={{ position: "absolute", width: "1px", height: "1px", overflow: "hidden", clip: "rect(0 0 0 0)" }}>
            {title}
          </Box>
          <Box component="thead">
            <Box component="tr">
              {columns.map((c) => (
                <Box component="th" scope="col" key={c.key} sx={{ ...cell, textAlign: align(c.key), fontWeight: 500, position: "sticky", top: 0, bgcolor: "background.paper" }}>
                  {c.header}
                </Box>
              ))}
            </Box>
          </Box>
          <Box component="tbody">
            {rows.map((row, r) => (
              <Box component="tr" key={r} sx={{ "&:hover > *": { bgcolor: "action.hover" } }}>
                {columns.map((c) => (
                  <Box
                    component="td"
                    key={c.key}
                    sx={{ ...cell, textAlign: align(c.key), fontVariantNumeric: "tabular-nums" }}
                  >
                    {row[c.key] ?? ""}
                  </Box>
                ))}
              </Box>
            ))}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};
